import React, { FC, useContext, useEffect, useMemo } from 'react';

import { Button, H2, Input, Modal } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import { IBaseModalProps } from '../interfaces';
import { IFormKaspiValues } from './interfaces';
import s from './KaspiOnlineModal.module.scss';

export const KaspiOnlineModal: FC<IBaseModalProps> = ({ onClose }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { kaspiOnlineBin, kaspiOnlineEnabled },
      isLoading,
    },
    actions: { setSetting, setSettings },
  } = useContext(SettingsContext);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<IFormKaspiValues>({
    mode: 'onChange',
    defaultValues: {
      bin: '',
    },
  });

  useEffect(() => reset({ bin: kaspiOnlineBin || '' }), [kaspiOnlineBin]);

  const disabledButton = useMemo<boolean>(
    () =>
      isDirty ||
      (!isDirty && !kaspiOnlineEnabled) ||
      !(Boolean(kaspiOnlineBin) && kaspiOnlineEnabled),
    [isDirty, kaspiOnlineEnabled, kaspiOnlineBin]
  );

  return (
    <Modal className={s.modal} onClose={onClose}>
      <div className={s.container}>
        <div className={s.header}>
          <H2>{t('settings.integration.kaspiOnline.title')}</H2>
          <span className={s.subtitle}>
            {t('settings.integration.kaspiOnline.description')}
          </span>
        </div>
        <form
          className={s.form}
          onSubmit={handleSubmit(({ bin }) => {
            if (kaspiOnlineEnabled) {
              return setSetting({
                alias: ESettingsAliases.KASPI_ONLINE_BIN,
                value: bin,
              }).then(onClose);
            } else if (!isDirty) {
              return setSetting({
                alias: ESettingsAliases.KASPI_ONLINE_ENABLED,
                value: '1',
              }).then(onClose);
            } else {
              return setSettings([
                {
                  alias: ESettingsAliases.KASPI_ONLINE_ENABLED,
                  value: '1',
                },
                {
                  alias: ESettingsAliases.KASPI_ONLINE_BIN,
                  value: bin,
                },
              ]).then(onClose);
            }
          })}
        >
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                placeholder={t('settings.kaspi.online.id')}
                variant="secondary"
                error={error?.message}
              />
            )}
            control={control}
            name="bin"
            rules={{
              required: t('errors.required'),
              pattern: {
                value: /^\d{12}$/g,
                message: t('errors.ean.length'),
              },
            }}
          />
          <div className={s.buttons_block}>
            <Button
              type="submit"
              variant="secondary"
              disabled={!disabledButton || isLoading}
              fullWidth
            >
              {kaspiOnlineBin && kaspiOnlineEnabled ? t('save') : t('hook')}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              disabled={!kaspiOnlineEnabled || isLoading}
              onClick={() => {
                setSetting({
                  alias: ESettingsAliases.KASPI_ONLINE_ENABLED,
                  value: '0',
                }).then(onClose);
              }}
            >
              {t('integration.offline')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
